import React, { useState, useEffect } from 'react';
import { rolesAPI } from '../../services/api';
import { useTenantScope } from '../../context/TenantScopeContext.jsx';
import './UserManagement.css'; // Reusing some base admin styles

export default function RolesManagement() {
    const [permissions, setPermissions] = useState([]);
    const [rolesData, setRolesData] = useState([]);
    const [selectedRole, setSelectedRole] = useState('supervisor');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    const { tenantId } = useTenantScope();

    useEffect(() => {
        loadData();
    }, [tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [pRes, rRes] = await Promise.all([
                rolesAPI.getPermissions(),
                rolesAPI.getRoles()
            ]);
            setPermissions(pRes.data.data);
            setRolesData(rRes.data.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const currentRole = rolesData.find(r => r.role === selectedRole);
    const rolePermissions = currentRole ? currentRole.permissions : [];

    const handleToggle = (permCode) => {
        const newRoles = rolesData.map(r => {
            if (r.role === selectedRole) {
                const newPerms = r.permissions.includes(permCode)
                    ? r.permissions.filter(p => p !== permCode)
                    : [...r.permissions, permCode];
                return { ...r, permissions: newPerms };
            }
            return r;
        });
        setRolesData(newRoles);
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        try {
            await rolesAPI.updatePermissions({
                role: selectedRole,
                permissions: rolePermissions
            });
            setMessage('Permisos actualizados correctamente');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setMessage('Error al guardar cambios');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading">Cargando matriz de permisos...</div>;

    return (
        <div className="container container-wide animate-in">
            <header className="admin-header">
                <div>
                    <h2>🛡️ Control de Roles y Permisos</h2>
                    <p className="text-muted">Define qué puede hacer cada perfil dentro de tu empresa.</p>
                </div>
            </header>

            <div className="grid grid-cols-12 gap-lg mt-lg">
                {/* Selector de Rol */}
                <div className="col-span-12 md:col-span-4">
                    <div className="glass-card p-md">
                        <h3 className="mb-md">Perfil a configurar</h3>
                        <div className="role-selector">
                            {['admin', 'supervisor', 'worker'].map(r => (
                                <button
                                    key={r}
                                    className={`role-btn ${selectedRole === r ? 'active' : ''}`}
                                    onClick={() => setSelectedRole(r)}
                                >
                                    {r === 'admin' ? '🏭 Administrador' : r === 'supervisor' ? '👥 Supervisor' : '👷 Operario'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Matriz de Permisos */}
                <div className="col-span-12 md:col-span-8">
                    <div className="glass-card p-md">
                        <div className="flex justify-between items-center mb-lg">
                            <h3 className="m-0">Matriz de Acceso: {selectedRole.toUpperCase()}</h3>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Guardando...' : 'Guardar Cambios'}
                            </button>
                        </div>

                        {message && (
                            <div className={`alert ${message.includes('Error') ? 'alert-danger' : 'alert-success'} mb-md`}>
                                {message}
                            </div>
                        )}

                        <div className="permissions-list">
                            {permissions.map(p => (
                                <div key={p.code} className="permission-item p-sm border-bottom hover-bg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-bold">{p.name}</div>
                                            <div className="text-xs text-muted">{p.description}</div>
                                        </div>
                                        <label className="switch">
                                            <input 
                                                type="checkbox" 
                                                checked={rolePermissions.includes(p.code)}
                                                onChange={() => handleToggle(p.code)}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .role-selector {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .role-btn {
                    padding: 12px;
                    border: 1px solid rgba(0,0,0,0.1);
                    background: white;
                    border-radius: 8px;
                    text-align: left;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .role-btn.active {
                    background: var(--color-primary);
                    color: white;
                    border-color: var(--color-primary);
                    font-weight: bold;
                }
                .hover-bg:hover {
                    background: rgba(0,0,0,0.02);
                }
                .switch {
                    position: relative;
                    display: inline-block;
                    width: 46px;
                    height: 24px;
                }
                .switch input { 
                    opacity: 0;
                    width: 0;
                    height: 0;
                }
                .slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background-color: #ccc;
                    transition: .4s;
                    border-radius: 34px;
                }
                .slider:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    transition: .4s;
                    border-radius: 50%;
                }
                input:checked + .slider {
                    background-color: var(--color-primary);
                }
                input:checked + .slider:before {
                    transform: translateX(22px);
                }
            `}</style>
        </div>
    );
}
